import { createContext, useCallback, useContext, useMemo } from 'react';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';

/**
 * One definition of this module's navigation, shared by both places it is mounted:
 * the production dashboard (nested under /email-marketing/verification, sub-nav as tabs)
 * and the module's own standalone entry point (mounted at /, sub-nav as a sidebar).
 *
 * Items are stored as URL *segments*, never absolute paths, so neither mount point has to
 * know about the other. Use useVerificationPath() to turn a segment into a link.
 *
 * The connection details (service address and access key) are deployment configuration, not a
 * user setting, so there is no Settings screen -- see api/client.js.
 */
export const NAV_SECTIONS = [
  {
    heading: 'Your lists',
    items: [
      {
        segment: '',
        label: 'Check an address',
        title: 'Check an address',
        caption: 'See whether one contact is safe to email before you send.',
        icon: MarkEmailReadOutlinedIcon,
      },
      {
        segment: 'batch',
        label: 'Clean a list',
        title: 'Clean a list',
        caption: 'Upload a list and get back the contacts worth keeping.',
        icon: CleaningServicesOutlinedIcon,
      },
      {
        segment: 'history',
        label: 'Results',
        title: 'Results',
        caption: 'Every contact you have checked, with what to do about each one.',
        icon: FactCheckOutlinedIcon,
      },
      {
        segment: 'analytics',
        label: 'List health',
        title: 'List health',
        caption: 'How clean your contact data is, and what it is costing you.',
        icon: MonitorHeartOutlinedIcon,
      },
    ],
  },
];

export const NAV_ITEMS = NAV_SECTIONS.flatMap((section) => section.items);

/** Joins the module's base path with a segment, e.g. ('/email-marketing/verification', 'batch'). */
export function verificationPath(base, segment = '') {
  const trimmed = (base || '/').replace(/\/+$/, '');
  if (!segment) return trimmed || '/';
  return `${trimmed}/${segment}`;
}

/**
 * Which nav item a pathname belongs to. Longest segment wins, so /batch/42 matches "Clean a
 * list" rather than falling through to the index item.
 */
export function activeNavItem(base, pathname) {
  const candidates = NAV_ITEMS.filter((item) => {
    const path = verificationPath(base, item.segment);
    return pathname === path || pathname.startsWith(`${path}/`);
  });
  return candidates.sort((a, b) => b.segment.length - a.segment.length)[0] || null;
}

const BaseContext = createContext('/');

/** Tells the module which URL it is mounted at; both entry points wrap their routes in this. */
export function VerificationBaseProvider({ base = '/', children }) {
  const value = useMemo(() => (base || '/').replace(/\/+$/, '') || '/', [base]);
  return <BaseContext.Provider value={value}>{children}</BaseContext.Provider>;
}

export function useVerificationBase() {
  return useContext(BaseContext);
}

/** Returns a `path(segment)` builder for links and navigate() calls inside the module. */
export function useVerificationPath() {
  const base = useVerificationBase();
  return useCallback((segment) => verificationPath(base, segment), [base]);
}
